import { DRAWING_QUESTION, drawingNoteHeader } from './drawingNoteVocab'
import { WorkbenchNoteBox, type NoteBoxClose } from './WorkbenchNoteBox'

/**
 * The comment box for a plain drawing — a trend line, arrow, channel, zone,
 * Fibonacci, and the rest of the rail's shapes.
 *
 * It is the SECOND of the two windows a selected drawing puts on the chart. The
 * first is `WorkbenchFloatingToolbar`, which sits above the shape and changes
 * how it LOOKS; this sits below it and records why it is there. The pair is the
 * point: restyling and explaining are different jobs, and neither should have to
 * dismiss the other.
 *
 * It carries no button, and that is not an omission. The trade-idea box has one
 * because an idea is a sequence with a step still owed; a drawing is not a step
 * in anything, so there is nothing to advance to. It carries no bin either — the
 * editing toolbar beside it already has one, and two bins for one drawing is one
 * bin too many.
 *
 * What the note is FOR: `chartContext.ts` renders it into the chart snapshot the
 * AI receives, as `— reason: "…"` on that drawing's line. Geometry alone tells
 * the model where the trader drew; this tells it why.
 */
export interface WorkbenchDrawingNoteProps {
  /** The klinecharts overlay name — decides what the header calls the shape. */
  name: string
  /** Opening position, relative to the chart wrapper. */
  x: number
  y: number
  initialValue?: string
  /**
   * Only a just-finished drawing takes the keyboard. Re-selecting a shape to
   * recolour it must not drag the caret out of whatever the trader was doing.
   */
  autoFocus?: boolean
  /** Debounced while typing. */
  onSave: (text: string) => void
  /** The ONE exit — every dismissal comes through here. */
  onCommit: (text: string, exit?: NoteBoxClose) => void
}

export function WorkbenchDrawingNote({
  name,
  x,
  y,
  initialValue = '',
  autoFocus = false,
  onSave,
  onCommit,
}: WorkbenchDrawingNoteProps) {
  return (
    <WorkbenchNoteBox<NoteBoxClose>
      title={drawingNoteHeader(name)}
      placeholder={DRAWING_QUESTION}
      x={x}
      y={y}
      initialValue={initialValue}
      autoFocus={autoFocus}
      markerAttr="data-drawing-note"
      onSave={onSave}
      onCommit={onCommit}
    />
  )
}
